import React, { useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import NameGeneratorModal from './NameGeneratorModal';
import ImagePicker from './ImagePicker';
import { BingoTheme, BINGO_THEMES } from '../types';

interface BingoFormProps {
  type: 'numbers' | 'names';
  setType: (type: 'numbers' | 'names') => void;
  names: string;
  setNames: (names: string) => void;
  title: string;
  setTitle: (title: string) => void;
  count: number;
  setCount: (count: number) => void;
  setImage: (image: string) => void;
  theme: BingoTheme;
  setTheme: (theme: BingoTheme) => void;
  onSubmit: () => void;
  onReset: () => void;
  image: string;
  isSubmitDisabled?: boolean;
}

const BingoForm: React.FC<BingoFormProps> = ({
  type, setType, names, setNames, title, setTitle, count, setCount, image, setImage, theme, setTheme, onSubmit, onReset, isSubmitDisabled
}) => {
  const [showGenerator, setShowGenerator] = useState(false);

  const handleClearAll = () => {
    if (window.confirm("Tem certeza que deseja limpar todo o formulário?")) {
      console.log("[DEBUG_LOG] BingoForm.handleClearAll - Clearing entire form");
      onReset();
    }
  };

  const onFormSubmit = (e: React.FormEvent) => {
    console.log("[DEBUG_LOG] BingoForm.onFormSubmit - Submitting form");
    e.preventDefault();

    if (type === 'names') {
      const namesList = names.split(',').map(n => n.trim()).filter(n => n !== '');
      if (namesList.length < 24) {
        console.log("[DEBUG_LOG] BingoForm.onFormSubmit - Insufficient names, blocking submit");
        return;
      }
    }

    onSubmit();
  };

  return (
    <Form onSubmit={onFormSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-secondary small text-uppercase">Título da Cartela</Form.Label>
            <Form.Control 
              type="text" 
              value={title} 
              placeholder="Ex: BINGO"
              onChange={(e) => {
                console.log("[DEBUG_LOG] BingoForm - Title changed");
                setTitle(e.target.value);
              }} 
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-secondary small text-uppercase">Tipo de Bingo</Form.Label>
            <div className="d-flex gap-2">
              <Button
                variant={type === 'numbers' ? "primary" : "outline-primary"}
                className="flex-fill"
                onClick={() => {
                  console.log("[DEBUG_LOG] BingoForm - Type changed to numbers");
                  setType('numbers');
                }}
                title="Números (1-75)"
              >
                Números
              </Button>
              <Button
                variant={type === 'names' ? "primary" : "outline-primary"}
                className="flex-fill"
                onClick={() => {
                  console.log("[DEBUG_LOG] BingoForm - Type changed to names");
                  setType('names');
                }}
                title="Nomes personalizados"
              >
                Nomes
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {type === 'names' && (
        <Form.Group className="mb-4 animate-fade-in">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="fw-bold text-secondary small text-uppercase">Nomes (separados por vírgula)</Form.Label>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="rounded-pill px-3"
                onClick={() => {
                  console.log("[DEBUG_LOG] BingoForm - Opening NameGeneratorModal");
                  setShowGenerator(true);
                }}
              >
                Gerar Nomes
              </Button>
            </div>
          </div>
          <Form.Control 
            as="textarea" 
            rows={4} 
            value={names} 
            placeholder="Ex: Amizade, Alegria, Saúde, Família..."
            className="shadow-sm"
            onChange={(e) => {
              console.log("[DEBUG_LOG] BingoForm - Names changed");
              setNames(e.target.value);
            }} 
          />
          <Form.Text className="fw-bold" style={{ color: 'var(--bingo-text-muted)' }}>
            Total de nomes: {names.split(',').map(n => n.trim()).filter(n => n !== '').length}
          </Form.Text>
        </Form.Group>
      )}

      <NameGeneratorModal 
        show={showGenerator} 
        onHide={() => setShowGenerator(false)} 
        onGenerate={(generatedNames) => {
          console.log("[DEBUG_LOG] BingoForm.onGenerate - Names generated, updating field");
          setNames(generatedNames);
        }}
      />

      <Row>
        <Col md={6}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-secondary small text-uppercase">Quantidade de Cartelas</Form.Label>
            <Form.Control 
              type="number" 
              value={count} 
              min={0}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                console.log("[DEBUG_LOG] BingoForm - Count changed to:", val);
                setCount(isNaN(val) ? 0 : val);
              }} 
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <ImagePicker image={image} setImage={setImage} />
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label className="fw-bold text-secondary small text-uppercase">Tema de Cores</Form.Label>
        <div className="theme-selector-container">
          {BINGO_THEMES.map((t) => (
            <div
              key={t.id}
              onClick={() => {
                console.log("[DEBUG_LOG] BingoForm - Theme changed to:", t.name);
                setTheme(t);
              }}
              className={`theme-selector-item ${theme.id === t.id ? 'active' : ''}`}
              style={{
                backgroundColor: t.primary,
                border: t.id === 'high-contrast' || t.id === 'default' ? '1px solid #ddd' : 'none',
                boxShadow: theme.id === t.id ? `0 0 0 3px white, 0 0 0 5px ${t.primary}` : 'none',
                ['--check-color' as any]: t.id === 'high-contrast' || t.id === 'default' ? 'black' : 'white'
              }}
              title={t.name}
            />
          ))}
        </div>
      </Form.Group>

    </Form>
  );
};

export default BingoForm;
